import { useCallback, useEffect, useState } from 'react';
import { LocalJsonStorage } from 'utils/storage';

export function buildPullSheetFilterKey(filterQuery) {
  if (!filterQuery?.start_date || !filterQuery?.end_date) {
    return 'default';
  }
  return [
    filterQuery.start_date,
    filterQuery.end_date,
    filterQuery.restaurant_ids ?? '',
    filterQuery.status ?? '',
    filterQuery.headcount_min ?? '',
    filterQuery.headcount_max ?? '',
  ].join('|');
}

function storageKey(organizationId, filterKey) {
  return `pull-sheet-checklist/${organizationId}/${filterKey}`;
}

function readStored(organizationId, filterKey) {
  if (!organizationId) {
    return {};
  }
  return LocalJsonStorage.get(storageKey(organizationId, filterKey)) || {};
}

export default function usePullSheetChecklist(organizationId, filterKey) {
  const [checkedByIngredient, setCheckedByIngredient] = useState(
    () => readStored(organizationId, filterKey),
  );

  useEffect(() => {
    setCheckedByIngredient(readStored(organizationId, filterKey));
  }, [organizationId, filterKey]);

  const persist = useCallback((nextOrUpdater) => {
    setCheckedByIngredient((prev) => {
      const next = typeof nextOrUpdater === 'function'
        ? nextOrUpdater(prev)
        : nextOrUpdater;
      if (organizationId) {
        LocalJsonStorage.set({
          key: storageKey(organizationId, filterKey),
          value: next,
        });
      }
      return next;
    });
  }, [organizationId, filterKey]);

  const isChecked = useCallback((ingredientId) => (
    Boolean(checkedByIngredient[ingredientId])
  ), [checkedByIngredient]);

  const setChecked = useCallback((ingredientId, checked) => {
    persist((prev) => {
      const next = { ...prev };
      if (checked) {
        next[ingredientId] = true;
      } else {
        delete next[ingredientId];
      }
      return next;
    });
  }, [persist]);

  const setManyChecked = useCallback((ingredientIds, checked) => {
    persist((prev) => {
      const next = { ...prev };
      ingredientIds.forEach((ingredientId) => {
        if (checked) {
          next[ingredientId] = true;
        } else {
          delete next[ingredientId];
        }
      });
      return next;
    });
  }, [persist]);

  const getProgress = useCallback((totalCount) => {
    const checked = Object.keys(checkedByIngredient).length;
    return { checked, total: totalCount };
  }, [checkedByIngredient]);

  return {
    isChecked,
    setChecked,
    setManyChecked,
    getProgress,
  };
}
